import { useAppForm } from '@/components/form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@e2n/ui';
import { ProgressCircle } from '@/domain/vertraege/features/VertragCarousel/card/ProgressCircle';
import { useSchema } from '@/domain/vertraege/features/VertragUpgraden/schema';
import { useVertraegeUpgraden } from '@/domain/vertraege/shared/hooks/useVertraegeUpgraden';
import {
  Vertrag,
  VertragStatus,
  VertragTarif,
} from '@/domain/vertraege/shared/types/vertrag';
import { useProfile } from '@/hooks';
import { toast } from '@/hooks/use-toast';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

// eslint-disable-next-line max-lines-per-function, complexity
export function VertragUpgraden({
  vertrag,
  kontingent,
  preis,
  onSuccess,
}: {
  vertrag: Vertrag;
  kontingent: number;
  preis: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { manager } = useProfile();
  const [open, setOpen] = useState(false);
  const [upgraden, { loading }] = useVertraegeUpgraden();

  const schema = useSchema({ t });

  const form = useAppForm({
    defaultValues: {
      telefon: '',
      beschreibung: '',
    } satisfies z.infer<typeof schema> as z.infer<typeof schema>,
    validators: {
      onChange: schema,
    },
    onSubmit: ({ value }) => {
      upgraden({
        input: {
          telefon: value.telefon,
          upgradeVersendetAm: dayjs().toISOString(),
          upgradeZum: vertrag.faelligkeitsdatum
            ? dayjs(vertrag.faelligkeitsdatum).toISOString()
            : undefined,
          beschreibung: value.beschreibung,
          vertragId: vertrag.id,
          kundennummer: vertrag.kundennummer.toString(),
          kontingent: kontingent,
          preis: preis,
          vorname: manager?.vorname ?? '',
          nachname: manager?.nachname ?? '',
          unternehmen: vertrag.unternehmensname,
        },
      }).then((result) => {
        if (result.data?.vertraege_upgraden === true) {
          toast({
            title: t('domain.vertraege.upgradeForm.submit.successTitel'),
            description: t(
              'domain.vertraege.upgradeForm.submit.successDescription',
            ),
          });
          setOpen(false);
          onSuccess();
          form.reset();
        } else {
          toast({
            variant: 'destructive',
            title: t('domain.vertraege.upgradeForm.submit.errorTitle'),
            description: t(
              'domain.vertraege.upgradeForm.submit.errorDescription',
            ),
          });
        }
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          disabled={
            kontingent < vertrag.aktiveAnzahl ||
            vertrag.status === VertragStatus.Beendet ||
            vertrag.status === VertragStatus.Gekuendigt
          }>
          {t('domain.vertraege.upgradeInfo.actions.bestaetigen')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('domain.vertraege.upgradeForm.titel')}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.name.label')}
          </div>
          <div className="font-semibold">{`${manager?.vorname ?? ''} ${manager?.nachname ?? ''}`}</div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.vertragsnummer.label')}
          </div>
          <div className="font-semibold">W-{vertrag.id}</div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.kundenummer.label')}
          </div>
          <div className="font-semibold">K-{vertrag.kundennummer}</div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.kontingent.label')}
          </div>
          <div className="flex gap-3 items-center">
            <ProgressCircle
              vertrag={vertrag}
              percentage={Math.round((vertrag.aktiveAnzahl / kontingent) * 100)}
            />
            <div className="w-2/3">
              <div className="w-full font-medium">
                <span className="text-neutrals-800 dark:text-white text-xl">
                  {vertrag.aktiveAnzahl}
                </span>
                <span className="text-neutrals-700 dark:text-white"> / </span>
                <span className="text-neutrals-700 text-xs">{kontingent}</span>
              </div>
              <div className="text-xs">
                {vertrag.vertragTarif === VertragTarif.MITARBEITER
                  ? t('domain.vertraege.slide.employeeTitle')
                  : t('domain.vertraege.slide.locationTitle')}
              </div>
            </div>
          </div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.betrag.label')}
          </div>
          <div className="font-semibold">{preis}</div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.abrechnungsBeginn.label')}
          </div>
          <div className="flex flex-col">
            <div className="font-semibold">
              {dayjs(vertrag.faelligkeitsdatum).format('DD.MM.YYYY')}
            </div>
            <div className="text-xs text-neutrals-700">
              {t('domain.vertraege.upgradeForm.abrechnungsBeginn.info')}
            </div>
          </div>
          <div className="text-neutrals-700">
            {t('domain.vertraege.upgradeForm.intervall.label')}
          </div>
          <div className="flex flex-col">
            <div className="font-semibold">
              <div className="text-sm font-bold">
                {vertrag.vertragIntervall === 'JAHR'
                  ? t('domain.vertraege.slide.yearly')
                  : t('domain.vertraege.slide.monthly')}
              </div>
            </div>
            {vertrag.vertragIntervall === 'JAHR' && (
              <div className="text-xs text-neutrals-700">
                {t('domain.vertraege.upgradeForm.intervall.info')}
              </div>
            )}
          </div>
        </div>
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex flex-col gap-4 mt-4">
            <div className="flex gap-4 w-full">
              <form.AppField name="telefon">
                {(field) => (
                  <field.TextInput
                    label={t(
                      'domain.vertraege.kuendigenForm.fields.telefon.label',
                    )}
                    placeholder="Telefonnummer eingeben"
                    type="text"
                    autoComplete="off"
                    data-1p-ignore
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="beschreibung">
              {(field) => (
                <field.TextArea
                  label={t(
                    'domain.vertraege.kuendigenForm.fields.beschreibung.label',
                  )}
                />
              )}
            </form.AppField>

            <div className="flex justify-end mt-4">
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isTouched]}>
                {([canSubmit, isTouched]) => (
                  <Button
                    type="submit"
                    disabled={!isTouched || !canSubmit || loading}
                    variant="default">
                    {t('domain.vertraege.upgradeForm.submit.label')}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
